import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MatchScore() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [player1Score, setPlayer1Score] = useState('');
  const [player2Score, setPlayer2Score] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadMatchData();
  }, [matchId]);

  const loadMatchData = async () => {
    try {
      const [userRes, matchesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/matches/my-matches`, { withCredentials: true })
      ]);
      
      setCurrentUser(userRes.data);
      const foundMatch = matchesRes.data.find(m => m.match_id === matchId);
      if (foundMatch) {
        setMatch(foundMatch);
        if (foundMatch.player1_score !== null) {
          setPlayer1Score(foundMatch.player1_score.toString());
          setPlayer2Score(foundMatch.player2_score.toString());
        }
      } else {
        toast.error('Match not found');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load match data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterScore = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.put(
        `${API}/matches/${matchId}/enter-score`,
        {
          player1_score: parseInt(player1Score),
          player2_score: parseInt(player2Score)
        },
        { withCredentials: true }
      );
      toast.success('Score entered! Waiting for opponent confirmation');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter score');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmScore = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/matches/${matchId}/confirm`, {}, { withCredentials: true });
      toast.success('Score confirmed! Rankings updated');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm score');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineScore = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/matches/${matchId}/decline`, {}, { withCredentials: true });
      toast.success('Score declined. Please re-enter');
      loadMatchData();
    } catch (error) {
      toast.error('Failed to decline score');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading match...</p>
        </div>
      </div>
    );
  }

  const canEnterScore = match?.status === 'pending_score';
  const canConfirm = match?.status === 'pending_confirmation' && match?.score_entered_by !== currentUser?.user_id;
  const isConfirmed = match?.status === 'confirmed';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="text-white hover:bg-white/10 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            Match Details
          </h1>
          <Badge className="bg-[#007AFF]/20 text-[#007AFF] rounded-full">
            {match?.list_name}
          </Badge>
        </div>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Match Score</CardTitle>
              <Badge 
                className={`${
                  isConfirmed ? 'bg-green-500/20 text-green-400' :
                  match?.status === 'pending_confirmation' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-blue-500/20 text-blue-400'
                } rounded-full`}
              >
                {match?.status.replace('_', ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Versus Display */}
            <div 
              className="grid grid-cols-3 gap-6 mb-8 p-6 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(0,122,255,0.1) 0%, rgba(255,59,48,0.1) 100%)'
              }}
            >
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Player 1</p>
                <p className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {match?.player1_name}
                </p>
                {canEnterScore ? (
                  <Input
                    data-testid="player1-score-input"
                    type="number"
                    min="0"
                    value={player1Score}
                    onChange={(e) => setPlayer1Score(e.target.value)}
                    className="bg-transparent border-b-2 border-[#007AFF] text-center text-5xl font-black text-[#007AFF] outline-none w-32 mx-auto"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    placeholder="0"
                  />
                ) : (
                  <div className="text-5xl font-black text-[#007AFF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {match?.player1_score ?? '-'}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center">
                <span className="text-2xl font-semibold text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>VS</span>
              </div>

              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Player 2</p>
                <p className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {match?.player2_name}
                </p>
                {canEnterScore ? (
                  <Input
                    data-testid="player2-score-input"
                    type="number"
                    min="0"
                    value={player2Score}
                    onChange={(e) => setPlayer2Score(e.target.value)}
                    className="bg-transparent border-b-2 border-[#FF3B30] text-center text-5xl font-black text-[#FF3B30] outline-none w-32 mx-auto"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    placeholder="0"
                  />
                ) : (
                  <div className="text-5xl font-black text-[#FF3B30]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {match?.player2_score ?? '-'}
                  </div>
                )}
              </div>
            </div>

            {/* Match Timestamps */}
            <div className="mb-6 flex flex-wrap gap-4 justify-center text-sm">
              {match?.challenged_at && (
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2">
                  <span className="text-gray-500">Challenged: </span>
                  <span className="text-white">{new Date(match.challenged_at).toLocaleString()}</span>
                </div>
              )}
              {match?.created_at && (
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2">
                  <span className="text-gray-500">Accepted: </span>
                  <span className="text-white">{new Date(match.created_at).toLocaleString()}</span>
                </div>
              )}
              {match?.confirmed_at && (
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2">
                  <span className="text-gray-500">Confirmed: </span>
                  <span className="text-green-400">{new Date(match.confirmed_at).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            {canEnterScore && (
              <form onSubmit={handleEnterScore}>
                <Button 
                  type="submit"
                  data-testid="submit-score-btn"
                  disabled={isSubmitting || !player1Score || !player2Score}
                  className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold py-6"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Score'}
                </Button>
              </form>
            )}

            {canConfirm && (
              <div className="space-y-3">
                <p className="text-center text-gray-400 mb-4">
                  Opponent entered the score. Please confirm or decline.
                </p>
                <Button 
                  data-testid="confirm-score-btn"
                  onClick={handleConfirmScore}
                  disabled={isSubmitting}
                  className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full font-bold py-6"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Confirm Score
                </Button>
                <Button 
                  onClick={handleDeclineScore}
                  disabled={isSubmitting}
                  variant="outline"
                  className="w-full border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full font-bold py-6"
                >
                  <X className="w-5 h-5 mr-2" />
                  Decline Score
                </Button>
              </div>
            )}

            {isConfirmed && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-400 font-bold text-lg mb-2">Match Confirmed!</p>
                <p className="text-gray-400 text-sm">Rankings have been updated</p>
                {match?.elo_change_p1 && (
                  <div className="mt-4 flex justify-center gap-6">
                    <div>
                      <p className="text-xs text-gray-500">{match.player1_name}</p>
                      <p className={`font-black ${match.elo_change_p1 > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {match.elo_change_p1 > 0 ? '+' : ''}{match.elo_change_p1}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{match.player2_name}</p>
                      <p className={`font-black ${match.elo_change_p2 > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {match.elo_change_p2 > 0 ? '+' : ''}{match.elo_change_p2}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {match?.status === 'pending_confirmation' && match?.score_entered_by === currentUser?.user_id && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
                <p className="text-yellow-400 font-bold">Waiting for opponent to confirm score</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MatchScore;